import { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Globe, MapPin, Handshake, Rocket, ArrowRight } from "lucide-react";
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from "react-simple-maps";

/**
 * Our Reach — real-world interactive map (TopoJSON countries) with pins for
 * the 7 target markets. Each pin is clickable to reveal the local deployment
 * story in the right-hand card. The user can also pan/zoom via wheel + drag.
 *
 * TopoJSON source: world-atlas v2 (public domain, served from unpkg CDN).
 */

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

// ISO-3 codes used by world-atlas 110m. Highlighted countries get a brand tint.
const COUNTRY_ISO = {
    "south-africa": "710",
    "usa": "840",
    "uk": "826",
    "uae": "784",
    "canada": "124",
    "japan": "392",
    "china": "156",
};
const HIGHLIGHTED_IDS = new Set(Object.values(COUNTRY_ISO));

const PINS = [
    {
        id: "south-africa",
        name: "South Africa",
        flag: "🇿🇦",
        coordinates: [28.0473, -26.2041],   // Johannesburg
        tier: "home",
        label: "HOME MARKET · ACTIVE",
    },
    {
        id: "usa",
        name: "United States",
        flag: "🇺🇸",
        coordinates: [-95.7129, 37.0902],
        tier: "pilot",
        label: "PILOT · 2026",
    },
    {
        id: "uk",
        name: "United Kingdom",
        flag: "🇬🇧",
        coordinates: [-0.1276, 51.5074],   // London
        tier: "office",
        label: "LONDON OFFICE · 2026",
    },
    {
        id: "uae",
        name: "Dubai, UAE",
        flag: "🇦🇪",
        coordinates: [55.2708, 25.2048],
        tier: "pilot",
        label: "GCC PILOT · 2026",
    },
    {
        id: "canada",
        name: "Canada",
        flag: "🇨🇦",
        coordinates: [-106.3468, 56.1304],
        tier: "exploring",
        label: "ACTIVE DISCUSSIONS",
    },
    {
        id: "japan",
        name: "Japan",
        flag: "🇯🇵",
        coordinates: [138.2529, 36.2048],
        tier: "exploring",
        label: "ACTIVE DISCUSSIONS",
    },
    {
        id: "china",
        name: "China",
        flag: "🇨🇳",
        coordinates: [104.1954, 35.8617],
        tier: "pilot",
        label: "MANUFACTURING + PILOT",
    },
];

const TIER_COLOR = {
    home: "bg-[var(--medx-red)]",
    pilot: "bg-[var(--medx-orange)]",
    office: "bg-amber-500",
    exploring: "bg-sky-500",
};

const PIN_HEX = {
    home: "#e63946",
    pilot: "#f77f00",
    office: "#f59e0b",
    exploring: "#38bdf8",
};

export default function OurReach() {
    const { t } = useTranslation();
    const [active, setActive] = useState(PINS[0]);

    return (
        <div className="bg-white min-h-screen" data-testid="our-reach-page">
            <Navbar />
            <main className="pt-28 pb-20">
                {/* -------- Hero -------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-10">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-4">
                        {t("ourReach.eyebrow")}
                    </div>
                    <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tighter leading-[0.95] mb-5">
                        {t("ourReach.titleLine1")}
                        <br />
                        <span className="brand-gradient-text">{t("ourReach.titleLine2")}</span>
                    </h1>
                    <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
                        {t("ourReach.intro")}
                    </p>
                </section>

                {/* -------- Interactive map -------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
                    <div className="rounded-3xl bg-gradient-to-br from-[#0b1021] via-[#121a36] to-[#0b1021] p-6 md:p-10 relative overflow-hidden">
                        {/* decorative glow */}
                        <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full bg-[var(--medx-orange)]/20 blur-3xl" />
                        <div className="absolute -left-32 -bottom-32 w-96 h-96 rounded-full bg-[var(--medx-red)]/20 blur-3xl" />

                        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-6">
                            {/* Map */}
                            <div className="lg:col-span-8">
                                <WorldMap pins={PINS} active={active} onPinClick={setActive} />
                                <Legend />
                            </div>

                            {/* Detail card */}
                            <div className="lg:col-span-4">
                                <DetailCard pin={active} t={t} />
                            </div>
                        </div>
                    </div>
                </section>

                {/* -------- Deployment narrative -------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 pb-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <StoryCard
                        icon={Globe}
                        title={t("ourReach.narrative.card1.title")}
                        body={t("ourReach.narrative.card1.body")}
                    />
                    <StoryCard
                        icon={Rocket}
                        title={t("ourReach.narrative.card2.title")}
                        body={t("ourReach.narrative.card2.body")}
                    />
                    <StoryCard
                        icon={MapPin}
                        title={t("ourReach.narrative.card3.title")}
                        body={t("ourReach.narrative.card3.body")}
                    />
                </section>

                {/* -------- Partner invitation CTA -------- */}
                <section className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--medx-red)] via-[#d84315] to-[var(--medx-orange)] text-white p-10 md:p-14 grain">
                        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
                        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-8">
                                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/40 backdrop-blur text-xs font-mono tracking-[0.18em] mb-5 text-white font-bold" data-testid="partners-wanted-badge">
                                    <Handshake className="w-3.5 h-3.5 text-white" /> {t("ourReach.partner.eyebrow")}
                                </div>
                                <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tighter mb-4 text-white">
                                    {t("ourReach.partner.title")}
                                </h2>
                                <p className="text-white/90 text-base md:text-lg max-w-2xl leading-relaxed">
                                    {t("ourReach.partner.body")}
                                </p>
                            </div>
                            <div className="lg:col-span-4 flex flex-col gap-3">
                                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white text-[var(--medx-red)] font-semibold hover:bg-orange-50 transition shadow-lg" data-testid="partner-contact-cta">
                                    <Handshake className="w-4 h-4" /> {t("ourReach.partner.ctaContact")}
                                </Link>
                                <Link to="/order" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/50 text-white font-semibold hover:bg-white/20 transition" data-testid="partner-order-cta">
                                    {t("ourReach.partner.ctaOrder")} <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

// ---------- Real-world interactive map ----------
function WorldMap({ pins, active, onPinClick }) {
    return (
        <div className="relative w-full rounded-2xl overflow-hidden bg-[#0b1021]" data-testid="world-map">
            <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 130, center: [10, 15] }}
                width={1000}
                height={520}
                style={{ width: "100%", height: "auto" }}
                aria-label="Medx AI deployment map"
            >
                <ZoomableGroup zoom={1} minZoom={1} maxZoom={5} center={[10, 15]}>
                    <Geographies geography={GEO_URL}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const highlighted = HIGHLIGHTED_IDS.has(String(geo.id));
                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        style={{
                                            default: {
                                                fill: highlighted ? "#f77f00" : "#1e293b",
                                                stroke: highlighted ? "#ffffff" : "#334155",
                                                strokeWidth: highlighted ? 0.8 : 0.5,
                                                outline: "none",
                                            },
                                            hover: {
                                                fill: highlighted ? "#fb923c" : "#334155",
                                                stroke: "#64748b",
                                                strokeWidth: 0.8,
                                                outline: "none",
                                                cursor: "pointer",
                                            },
                                            pressed: { fill: highlighted ? "#fb923c" : "#475569", outline: "none" },
                                        }}
                                    />
                                );
                            })
                        }
                    </Geographies>
                    {pins.map((p) => (
                        <MapPinMarker
                            key={p.id}
                            pin={p}
                            isActive={active.id === p.id}
                            onClick={() => onPinClick(p)}
                        />
                    ))}
                </ZoomableGroup>
            </ComposableMap>
            <div className="absolute bottom-3 right-3 bg-black/40 backdrop-blur text-white/80 text-[10px] font-mono tracking-wider px-3 py-1.5 rounded-full border border-white/15 pointer-events-none">
                SCROLL TO ZOOM · DRAG TO PAN
            </div>
        </div>
    );
}

function MapPinMarker({ pin, isActive, onClick }) {
    const color = PIN_HEX[pin.tier] || "#38bdf8";
    const r = isActive ? 7 : 5;
    return (
        <Marker
            coordinates={pin.coordinates}
            onClick={onClick}
            style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" }, pressed: { cursor: "pointer" } }}
            data-testid={`map-pin-${pin.id}`}
        >
            {isActive && (
                <circle r="14" fill={color} opacity="0.3">
                    <animate attributeName="r" from="8" to="18" dur="1.4s" repeatCount="indefinite" />
                    <animate attributeName="opacity" from="0.5" to="0" dur="1.4s" repeatCount="indefinite" />
                </circle>
            )}
            <circle r={r} fill={color} stroke="#fff" strokeWidth="1.5" />
            <text
                textAnchor="middle"
                y={-10}
                style={{ fontSize: 9, fontWeight: 700, fill: "#fff", pointerEvents: "none", userSelect: "none" }}
            >
                {pin.name}
            </text>
        </Marker>
    );
}

function Legend() {
    const items = [
        { color: "bg-[var(--medx-red)]", label: "Home market — Active" },
        { color: "bg-[var(--medx-orange)]", label: "Pilot deployment" },
        { color: "bg-amber-500", label: "Regional office" },
        { color: "bg-sky-500", label: "Active discussions" },
    ];
    return (
        <div className="mt-4 flex flex-wrap gap-4 text-[11px] text-white/80">
            {items.map((i) => (
                <div key={i.label} className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${i.color}`} />
                    {i.label}
                </div>
            ))}
        </div>
    );
}

function DetailCard({ pin, t }) {
    return (
        <div className="rounded-2xl bg-white/5 border border-white/15 p-6 backdrop-blur min-h-[320px] flex flex-col" data-testid="reach-detail-card">
            <div className="flex items-center gap-3 mb-4">
                <span className={`inline-block w-3 h-3 rounded-full ${TIER_COLOR[pin.tier] || "bg-white"}`} />
                <span className="text-[10px] font-mono tracking-[0.22em] text-white/60">{pin.label}</span>
            </div>
            <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">{pin.flag}</span>
                <h3 className="font-display text-2xl font-semibold text-white">{pin.name}</h3>
            </div>
            <p className="text-sm text-white/75 leading-relaxed flex-1">
                {t(`ourReach.pins.${pin.id}.body`)}
            </p>
            <div className="mt-5 pt-4 border-t border-white/10 text-[11px] font-mono tracking-[0.18em] text-white/50 flex justify-between">
                <span>{t(`ourReach.pins.${pin.id}.statLabel`)}</span>
                <span className="text-white">{t(`ourReach.pins.${pin.id}.stat`)}</span>
            </div>
        </div>
    );
}

function StoryCard({ icon: Icon, title, body }) {
    return (
        <div className="card-soft p-6">
            <div className="w-11 h-11 rounded-xl bg-orange-50 text-[var(--medx-red)] grid place-items-center mb-4">
                <Icon className="w-5 h-5" />
            </div>
            <div className="font-display text-xl font-semibold mb-2">{title}</div>
            <p className="text-sm text-gray-600 leading-relaxed">{body}</p>
        </div>
    );
}
